import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import momentTz from 'moment-timezone';

import TeamBadge from '../TeamBadge/TeamBadge';
import { DASHBOARD_STACK } from '../../utils/screenNames';
import { computeTournamentStatus } from '../../utils/tournamentStatus';

const formatTime = (matchTime, timezone) => {
  if (!matchTime) return '—';
  const m = timezone ? momentTz(matchTime).tz(timezone) : momentTz(matchTime);
  return m.format('h:mm A');
};

const formatScore = (fixture) => {
  if (fixture.homeTeamScore == null || fixture.awayTeamScore == null) return '—';
  return `${fixture.homeTeamScore} - ${fixture.awayTeamScore}`;
};

const FixtureRow = ({ fixture, valueText }) => (
  <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-3 border-b border-app-border px-3 py-4">
    <div className="flex items-center gap-2 min-w-0">
      <TeamBadge
        src={fixture.homeTeam?.crest}
        alt={fixture.homeTeam?.title || 'Home'}
        containerSize="w-7 h-7"
        size="w-3 h-3"
      />
      <span className="text-app-text text-[13px] truncate">
        {fixture.homeTeam?.title || 'TBD'}
      </span>
    </div>
    <div className="flex items-center gap-2 min-w-0">
      <TeamBadge
        src={fixture.awayTeam?.crest}
        alt={fixture.awayTeam?.title || 'Away'}
        containerSize="w-7 h-7"
        size="w-3 h-3"
      />
      <span className="text-app-text text-[13px] truncate">
        {fixture.awayTeam?.title || 'TBD'}
      </span>
    </div>
    <span className="text-app-text text-[13px] font-medium tabular-nums whitespace-nowrap">
      {valueText}
    </span>
  </div>
);

const FixturesCard = ({ title, fixtures, emptyText, seeMorePath, columnLabel, getValue }) => {
  const navigate = useNavigate();
  const isEmpty = !fixtures || fixtures.length === 0;

  return (
    <div className="rounded-2xl flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-app-text text-[18px] font-medium m-0">{title}</h3>
        <button
          type="button"
          onClick={() => navigate(seeMorePath)}
          className="text-app-text-secondary text-[13px] underline hover:text-app-text transition-colors cursor-pointer"
        >
          See more
        </button>
      </div>

      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center rounded-2xl border border-app-border overflow-auto">
          <p className="text-app-text-secondary text-[14px] m-0">{emptyText}</p>
        </div>
      ) : (
        <div className="flex flex-col rounded-2xl border border-app-border overflow-auto">
          <div className="grid grid-cols-[1fr_1fr_auto] bg-app-surface items-center gap-3 px-3 py-4 text-[12px] text-app-text-secondary">
            <span>Team 1</span>
            <span>Team 2</span>
            <span>{columnLabel}</span>
          </div>
          {fixtures.map((fixture) => (
            <FixtureRow
              key={fixture._id}
              fixture={fixture}
              valueText={getValue(fixture)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardFixturesRow = () => {
  const { selectedTournament, dashboard } = useSelector((state) => state.tournament);
  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const timezone = selectedTournament?.timezone;
  const isFresh = dashboard && dashboard.tournamentId === tournamentId;

  const upcoming = isFresh ? dashboard.upcomingFixtures : null;
  const last = isFresh ? dashboard.lastFixtures : null;

  const status = useMemo(
    () => computeTournamentStatus(selectedTournament),
    [selectedTournament]
  );
  const isCompleted = status === 'COMPLETED';

  return (
    <div className={`grid grid-cols-1 ${isCompleted ? '' : 'lg:grid-cols-2'} gap-6`}>
      {!isCompleted && (
        <FixturesCard
          title="Upcoming fixtures"
          fixtures={upcoming}
          emptyText="No fixtures yet"
          seeMorePath={DASHBOARD_STACK.FIXTURES}
          columnLabel="Time"
          getValue={(fixture) => formatTime(fixture.matchTime, timezone)}
        />
      )}
      <FixturesCard
        title="Last fixtures"
        fixtures={last}
        emptyText="No results yet"
        seeMorePath={DASHBOARD_STACK.RESULTS}
        columnLabel="Score"
        getValue={(fixture) => formatScore(fixture)}
      />
    </div>
  );
};

export default DashboardFixturesRow;
